import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Star, Shield, Truck, RotateCcw, Headphones } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 9: NAKSHA
 * Style: Blueprint Modern — Geometric, tech-forward Indian fashion
 * Electric blue accents, sharp corners, clean typography
 */
const Template9 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const touchStartX = useRef(0);

    const categories = [
        {
            name: 'Traditional Weaves',
            description: 'Handcrafted textiles from master artisans',
            image: 'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=600'
        },
        {
            name: 'Contemporary Styles',
            description: 'Modern silhouettes for the new India',
            image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=600'
        },
        {
            name: 'Bridal Edit',
            description: 'Curated pieces for your special day',
            image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=600'
        },
        {
            name: 'Fusion Wear',
            description: 'Where tradition meets contemporary',
            image: 'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=600'
        }
    ];

    const products = [
        { id: 1, name: 'Chanderi Silk Saree', price: '\u20B98,999', tag: 'NEW', image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 2, name: 'Banarasi Kurta Set', price: '\u20B96,499', image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Embroidered Anarkali', price: '\u20B912,999', tag: 'NEW', image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 4, name: 'Handloom Cotton Dupatta', price: '\u20B92,499', image: 'https://images.pexels.com/photos/6712360/pexels-photo-6712360.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Silk Blend Lehenga', price: '\u20B918,500', tag: 'NEW', image: 'https://images.pexels.com/photos/7275385/pexels-photo-7275385.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 6, name: 'Zardozi Sherwani', price: '\u20B924,999', image: 'https://images.pexels.com/photos/8953474/pexels-photo-8953474.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 7, name: 'Block Print Palazzo Set', price: '\u20B94,999', image: 'https://images.pexels.com/photos/2955375/pexels-photo-2955375.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 8, name: 'Ikat Weave Kurta', price: '\u20B95,499', tag: 'NEW', image: 'https://images.pexels.com/photos/3819793/pexels-photo-3819793.jpeg?auto=compress&cs=tinysrgb&w=400' }
    ];

    const testimonials = [
        {
            text: 'The craftsmanship is impeccable. Every piece I have ordered reflects true artisan quality. The Chanderi saree drapes like a dream.',
            name: 'Priya Sharma',
            city: 'Mumbai',
            rating: 5
        },
        {
            text: 'NAKSHA has redefined how I shop for Indian wear. The geometric aesthetic of their curation matches my modern taste perfectly.',
            name: 'Arjun Mehta',
            city: 'Bangalore',
            rating: 5
        },
        {
            text: 'From bridal lehengas to everyday kurtas, the quality is consistently outstanding. Fast delivery and beautiful packaging too.',
            name: 'Kavitha Reddy',
            city: 'Hyderabad',
            rating: 5
        }
    ];

    const trustBadges = [
        { icon: Shield, label: 'Authentic Handcrafted' },
        { icon: Truck, label: 'Free Shipping \u20B9999+' },
        { icon: RotateCcw, label: '7-Day Returns' },
        { icon: Headphones, label: '24/7 Support' }
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) => diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
        }
    };

    const toggleWishlist = (e, id) => { e.stopPropagation(); setWishlist((prev) => ({ ...prev, [id]: !prev[id] })); };

    return (
        <div style={{ fontFamily: "'Space Grotesk', sans-serif" }} className="w-full min-h-screen bg-white">
            <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

            {/* ===== SECTION 1: TYPOGRAPHY HERO ===== */}
            <section className="relative w-full min-h-[90vh] md:min-h-screen overflow-hidden bg-white">
                {/* Geometric accent lines - Desktop */}
                <div className="absolute top-0 left-0 w-1 h-full bg-[#2563EB] hidden md:block" />
                <div className="absolute top-20 left-8 w-40 h-px bg-[#2563EB] hidden md:block" />
                <div className="absolute bottom-32 right-12 w-48 h-px bg-[#2563EB] hidden md:block" />
                <div className="absolute top-1/3 right-0 w-1 h-32 bg-[#2563EB] hidden md:block" />
                {/* Geometric accent lines - Mobile */}
                <div className="absolute top-0 left-0 w-0.5 h-full bg-[#2563EB] md:hidden" />
                <div className="absolute top-16 left-4 w-12 h-px bg-[#2563EB] md:hidden" />

                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center pt-24 pb-12 md:pt-0 md:pb-0 min-h-[90vh] md:min-h-screen">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center w-full">
                        {/* Left: Typography */}
                        <div className="order-2 md:order-1">
                            <p className="text-xs sm:text-sm tracking-[0.3em] uppercase mb-4 text-[#2563EB] font-medium">
                                Blueprint Collection 2026
                            </p>
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[0.95] mb-6 text-[#0F172A]">
                                MODERN
                                <br />
                                INDIAN
                                <br />
                                <span className="text-[#2563EB]">FASHION</span>
                            </h1>
                            <div className="w-16 h-0.5 bg-[#2563EB] mb-6" />
                            <p className="text-sm sm:text-base text-[#64748B] max-w-md mb-8 leading-relaxed font-light">
                                Precision-crafted Indian fashion that bridges heritage artistry with contemporary design. Every thread, every weave, engineered to perfection.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={() => navigate('/template9/shop')}
                                    className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-[#2563EB] text-white text-sm font-medium tracking-wider uppercase rounded-none transition-all duration-300 hover:bg-[#1D4ED8]"
                                >
                                    Explore Collection <ArrowRight className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => navigate('/template9/about')}
                                    className="inline-flex items-center justify-center gap-2 px-8 py-3.5 border-2 border-[#2563EB] text-[#2563EB] text-sm font-medium tracking-wider uppercase rounded-none transition-all duration-300 hover:bg-[#2563EB] hover:text-white"
                                >
                                    Our Story
                                </button>
                            </div>
                        </div>

                        {/* Right: Hero Image */}
                        <div className="order-1 md:order-2 relative">
                            <div className="relative overflow-hidden rounded-none">
                                <img
                                    src="https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=800"
                                    alt="Modern Indian Fashion"
                                    className="w-full h-[50vh] md:h-[70vh] object-cover"
                                />
                                {/* Blue corner accents */}
                                <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#2563EB]" />
                                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#2563EB]" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 2: CATEGORY GRID ===== */}
            <section className="py-16 sm:py-20 lg:py-28 bg-[#F8FAFC]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 md:mb-16">
                        <p className="text-xs sm:text-sm tracking-[0.3em] uppercase mb-3 text-[#2563EB] font-medium">
                            Categories
                        </p>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0F172A] mb-4">
                            Shop by Collection
                        </h2>
                        <div className="w-12 h-0.5 bg-[#2563EB] mx-auto" />
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                        {categories.map((cat, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template9/shop')}
                                className="group cursor-pointer relative overflow-hidden rounded-none border border-[#E2E8F0] hover:border-[#2563EB] transition-all duration-300"
                            >
                                <div className="aspect-[3/4] overflow-hidden">
                                    <img
                                        src={cat.image}
                                        alt={cat.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/80 via-transparent to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                                    <div className="w-0 group-hover:w-8 h-0.5 bg-[#2563EB] mb-2 transition-all duration-300" />
                                    <h3 className="text-base sm:text-lg font-semibold text-white mb-1">
                                        {cat.name}
                                    </h3>
                                    <p className="text-xs text-white/70 hidden sm:block">
                                        {cat.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 3: PRODUCT GRID ===== */}
            <section className="py-16 sm:py-20 lg:py-28 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-12 md:mb-16">
                        <div>
                            <p className="text-xs sm:text-sm tracking-[0.3em] uppercase mb-3 text-[#2563EB] font-medium">
                                Curated Selection
                            </p>
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0F172A]">
                                Featured Products
                            </h2>
                        </div>
                        <button
                            onClick={() => navigate('/template9/shop')}
                            className="mt-4 sm:mt-0 inline-flex items-center gap-2 text-sm font-medium text-[#2563EB] hover:text-[#1D4ED8] tracking-wider uppercase transition-colors duration-300"
                        >
                            View All <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template9/product')}
                                className="group cursor-pointer border border-[#E2E8F0] rounded-none overflow-hidden hover:border-l-2 hover:border-l-[#2563EB] transition-all duration-300 bg-white"
                            >
                                <div className="relative aspect-[3/4] overflow-hidden">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                    {product.tag && (
                                        <span className="absolute top-3 left-3 bg-[#2563EB] text-white text-[10px] sm:text-xs font-semibold px-2.5 py-1 tracking-wider uppercase rounded-none">
                                            {product.tag}
                                        </span>
                                    )}
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/90 hover:bg-white transition-colors duration-200 rounded-none"
                                    >
                                        <Heart
                                            className={`w-4 h-4 ${wishlist[product.id] ? 'fill-[#2563EB] text-[#2563EB]' : 'text-[#64748B]'}`}
                                        />
                                    </button>
                                </div>
                                <div className="p-3 sm:p-4">
                                    <h3 className="text-sm sm:text-base font-medium text-[#0F172A] mb-1 truncate">
                                        {product.name}
                                    </h3>
                                    <p className="text-sm sm:text-base font-semibold text-[#2563EB]">
                                        {product.price}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 4: CUSTOMER REVIEWS ===== */}
            <section className="py-16 sm:py-20 lg:py-28 bg-[#F8FAFC]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 md:mb-16">
                        <p className="text-xs sm:text-sm tracking-[0.3em] uppercase mb-3 text-[#2563EB] font-medium">
                            Testimonials
                        </p>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0F172A] mb-4">
                            What Our Customers Say
                        </h2>
                        <div className="w-12 h-0.5 bg-[#2563EB] mx-auto" />
                    </div>

                    {/* Mobile: Swipeable single card */}
                    <div
                        className="md:hidden"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        <div className="bg-white border border-[#E2E8F0] rounded-none p-6 relative">
                            <div className="absolute top-0 left-0 w-1 h-full bg-[#2563EB]" />
                            <div className="flex gap-1 mb-4">
                                {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                                    <Star key={i} className="w-4 h-4 fill-[#2563EB] text-[#2563EB]" />
                                ))}
                            </div>
                            <p className="text-sm text-[#0F172A] leading-relaxed mb-4 font-light">
                                "{testimonials[activeTestimonial].text}"
                            </p>
                            <div>
                                <p className="text-sm font-semibold text-[#0F172A]">
                                    {testimonials[activeTestimonial].name}
                                </p>
                                <p className="text-xs text-[#64748B]">
                                    {testimonials[activeTestimonial].city}
                                </p>
                            </div>
                            <div className="flex gap-2 mt-4 justify-center">
                                {testimonials.map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setActiveTestimonial(i)}
                                        className={`h-2 rounded-none transition-all duration-300 ${i === activeTestimonial ? 'bg-[#2563EB] w-6' : 'bg-[#E2E8F0] w-2'}`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Desktop: 3-column grid */}
                    <div className="hidden md:grid md:grid-cols-3 gap-6">
                        {testimonials.map((testimonial, index) => (
                            <div
                                key={index}
                                className="bg-white border border-[#E2E8F0] rounded-none p-6 lg:p-8 relative hover:border-[#2563EB] transition-colors duration-300"
                            >
                                <div className="absolute top-0 left-0 w-1 h-full bg-[#2563EB]" />
                                <div className="flex gap-1 mb-4">
                                    {[...Array(testimonial.rating)].map((_, i) => (
                                        <Star key={i} className="w-4 h-4 fill-[#2563EB] text-[#2563EB]" />
                                    ))}
                                </div>
                                <p className="text-sm lg:text-base text-[#0F172A] leading-relaxed mb-6 font-light">
                                    "{testimonial.text}"
                                </p>
                                <div>
                                    <p className="text-sm font-semibold text-[#0F172A]">
                                        {testimonial.name}
                                    </p>
                                    <p className="text-xs text-[#64748B]">
                                        {testimonial.city}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 5: TRUST BADGES ===== */}
            <section className="py-10 sm:py-14 bg-white border-t border-b border-[#E2E8F0]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                        {trustBadges.map((badge, index) => (
                            <div key={index} className="flex flex-col items-center text-center">
                                <badge.icon className="w-6 h-6 mb-3 text-[#2563EB]" strokeWidth={1.5} />
                                <p className="text-xs sm:text-sm font-medium text-[#0F172A] tracking-wide">
                                    {badge.label}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Template9;
